import "./FramePickerModal.css";

import { InboxOutlined } from "@ant-design/icons";
import { Form, Modal, Select, Button, message, Upload, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import PromptGenerator from "../../../../components/PromptGenerator/PromptGenerator";
import PromptChoices from "../../../../components/PromptChoices/PromptChoices";
import { useSelector } from "react-redux";
import { selectIdentityToken, selectSessionId, selectSessionToken } from "../../../../store/sessionSlice";
import { fetchRequest } from "../../../../utils/Fetcher";
import FrameImagePicker from "../FrameImagePicker/FrameImagePicker";
import { runSceneImageGenerationJob } from "../../../../utils/polls/RunSceneImageGenerationJob";

export default function FramePickerModal({ open, which, sceneId, storyboardId, selectedConceptIds, onCancel, onOk, generatedImagesPrompt = [] }) {
  const title = which === "firstFrame" ? "Select First Frame" : "Select Last Frame";

  const [messageApi, contextHolder] = message.useMessage();

  const sessionId = useSelector(selectSessionId);
  const sessionToken = useSelector(selectSessionToken);
  const accessToken = useSelector(selectIdentityToken);

  const frameOptions = [
    { label: "Generate Image", value: "generate-image" },
    { label: "Select from Generated Image", value: "selected-image" },
    { label: "Upload Image", value: "upload-image" },
  ];

  const [step, setStep] = useState(0);
  const [form] = Form.useForm();

  const [prompt, setPrompt] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState([]);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [refFileList, setRefFileList] = useState([]);     // for generate-image
  const [uploadFileList, setUploadFileList] = useState([]); // for upload-image

  const [genConfig, setGenConfig] = useState(null);

  const [referenceImages, setReferenceImages] = useState([]);
  const [selectedImageUri, setSelectedImageUri] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingDesc, setLoadingDesc] = useState("");

  const [selectedMode, setSelectedMode] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageKey, setSelectedImageKey] = useState("0");
  const [uploadedImages, setUploadedImages] = useState([]);

  const [localPreviewOpen, setLocalPreviewOpen] = useState(false);
  const [localPreviewSrc, setLocalPreviewSrc] = useState("");

  const [revisingKey, setRevisingKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null);

  const generatePrompt = async (refImages = [], values) => {
    setLoading(true);
    setLoadingDesc("AI is generating your image descriptions..");

    const body = {
      campaignConceptId: selectedConceptIds,
      prompt: values.description,
      aspectRatio: values.aspect_ratio,
      numOfCampaignImage: values.num_images,
      ...(refImages?.length > 0 ? { referenceImages: refImages } : {}),
    };

    const { result } = await fetchRequest({
      path: `sessions/campaign-image/prompts/generate`,
      method: "POST",
      data: JSON.stringify(body),
    }).finally(() => {
      setLoadingDesc("");
    });

    return result;
  };

  const getReferenceImagesPayload = (refImages = referenceImages) =>
    (refImages ?? []).length > 0 ? { referenceImages: refImages } : {};

  const buildSceneImageBody = ({ promptText, numImages = 1, refImages = referenceImages }) => ({
    storyboardId,
    prompt: promptText,
    aspectRatio: genConfig?.aspect_ratio ?? "16:9",
    numOfCampaignImage: numImages,
    ...getReferenceImagesPayload(refImages),
  });

  const generateSceneImage = async ({ promptText, numImages = 1, refImages = referenceImages }) => {
    setLoadingDesc("AI is generating campaign images..");

    const body = buildSceneImageBody({
      promptText,
      numImages,
      refImages,
    });

    const result = await runSceneImageGenerationJob({
      storyboardId,
      sceneId,
      body,
    });

    return result;
  };

  const normalizeImages = (payload) => {
    const list =
      payload?.result ??
      payload?.data ??
      payload?.items ??
      payload?.images ??
      payload?.generated_images ??
      payload?.generatedImages ??
      payload;

    if (Array.isArray(list)) return list;

    if (list && typeof list === "object") return [list];

    return [];
  };

  useEffect(() => {
    if (open) {
      setRevisingKey(null);
      setEditingKey(null);
    }
  }, [open]);

  useEffect(() => {
    setRevisingKey(null);
    setEditingKey(null);
  }, [selectedMode]);

  const handleRevisePrompt = async (key, instruction) => {
    try {
      setRevisingKey(key);

      const target = (prompt ?? []).find((p) => String(p?.id) === String(key));
      if (!target) return false;

      const { result } = await fetchRequest({
        path: `sessions/campaign-image/prompts/${target.id}/revise`,
        method: "PATCH",
        data: JSON.stringify({
          revisionInstructions: instruction,
        }),
      });

      const newDesc =
        result?.description ??
        result?.detailed_prompt ??
        result?.detailedPrompt ??
        result ??
        "";

      setPrompt((prev) =>
        (prev ?? []).map((p) =>
          String(p?.id) === String(key)
            ? { ...p, description: newDesc }
            : p
        )
      );

      return true;
    } catch (e) {
      messageApi.error(e?.message ?? "Failed to revise prompt");
      return false;
    } finally {
      setRevisingKey(null);
    }
  };

  const handleEditPrompt = async (key, { title, description }) => {
    try {
      setEditingKey(key);

      const target = (prompt ?? []).find((p) => String(p?.id) === String(key));
      if (!target) return false;

      const { result } = await fetchRequest({
        path: `sessions/campaign-image/prompts/${target.id}/edit`,
        method: "PATCH",
        data: JSON.stringify({
          title,
          prompt: description,
        }),
      });
      const nextTitle = result?.title ?? title;
      const nextDesc =
        result?.description ??
        result?.detailed_prompt ??
        result?.detailedPrompt ??
        description;

      setPrompt((prev) =>
        (prev ?? []).map((p) =>
          String(p?.id) === String(key)
            ? { ...p, title: nextTitle, description: nextDesc }
            : p
        )
      );

      messageApi.success("Prompt updated");
      return true;
    } catch (e) {
      messageApi.error(e?.message ?? "Failed to update prompt");
      return false;
    } finally {
      setEditingKey(null);
    }
  };

  useEffect(() => {
    if (open) {
      setStep(0);
      setSelectedMode(null);
    }
  }, [open]);

  useEffect(() => {
    setStep(0);
    setPrompt([]);
    setSelectedPrompt([]);
    setGeneratedImages([]);
    setRefFileList([]);
    setUploadFileList([]);
    setSelectedImageKey(null);
    setSelectedImage(null);
    setSelectedImageUri(null);
    setUploadedImages([]);
    setReferenceImages([]);
    setSelectedImageUri(null);
    setGenConfig(null);
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMode]);

  const totalSteps = useMemo(() => {
    if (selectedMode === "generate-image") return 3;
    if (selectedMode === "upload-image") return 2;
    return 1;
  }, [selectedMode]);

  const isLast = step === totalSteps - 1;
  const canNext = useMemo(() => {
    if (selectedMode === "upload-image" && step === 0) return uploadFileList.length > 0;
    if (selectedMode === "generate-image" && step === 1) return selectedPrompt.length > 0;
    return true;
  }, [selectedMode, step, uploadFileList, selectedPrompt]);

  const handleGenerateImageStep0 = async () => {
    const values = await form.validateFields();
    setGenConfig(values);

    const desc = (values.description ?? "").trim();
    if (!desc) {
      messageApi.warning("Please input the description.");
      return false;
    }

    setLoading(true);

    let refImages = [];
    if (refFileList.length > 0) {
      const gcsUris = await handleImageUpload(refFileList);
      refImages = gcsUris.map((uri) => ({ uri }));
      setReferenceImages(refImages);
    }

    const prompts = await generatePrompt(refImages, values);
    if (!prompts) return false;

    setPrompt(prompts ?? []);
    setSelectedPrompt([]);
    setGeneratedImages([]);

    return true;
  };

  const handleGenerateImageStep1 = async () => {
    setLoading(true);

    if (!selectedPrompt?.length) {
      messageApi.warning("Please select at least one image description.");
      return false;
    }

    const byId = new Map((prompt ?? []).map((p) => [String(p?.id), p]));
    const all = [];

    for (const pid of selectedPrompt) {
      const chosen = byId.get(String(pid));
      const desc = (chosen?.description ?? "").trim();
      if (!desc) continue;

      const result = await generateSceneImage({
        promptText: desc,
        numImages: 1,
      });

      const imgs = normalizeImages(result).map((img) => ({
        ...img,
        promptId: pid,
      }));

      all.push(...imgs);
    }

    if (!all.length) {
      messageApi.error("No images generated");
      return false;
    }

    setGeneratedImages(all);
    setSelectedImageKey("0");
    return true;
  };

  const handleNext = async () => {
    try {
      if (selectedMode === "generate-image") {
        if (step === 0) {
          const success = await handleGenerateImageStep0();
          if (success) {
            setStep((s) => Math.min(s + 1, totalSteps - 1));
          }
          return;
        }

        if (step === 1) {
          const success = await handleGenerateImageStep1();
          if (success) {
            setStep((s) => Math.min(s + 1, totalSteps - 1));
          }
          return;
        }
      }

      if (selectedMode === "upload-image") {
        if (step === 0) {
          setLoading(true);
          await handleUploadAndGoNext();
          return;
        }
      }

      // Default behavior for other steps/modes
      setStep((s) => Math.min(s + 1, totalSteps - 1));
    } catch (e) {
      console.error(e);
      message.error(e?.message ?? "Something went wrong");
    } finally {
      setLoading(false);
      setLoadingDesc("");
    }
  };

  const handleBack = () => {
    setStep((s) => Math.max(s - 1, 0));

    if (selectedMode === "upload-image" && step === 1) {
      setUploadedImages([]);
      setSelectedImageUri(null);
      setSelectedImageKey("0");
    }
  };

  const handleUse = () => {
    if (!selectedImageKey || !selectedImageUri) {
      messageApi.warning("Please select an image");
      return;
    }

    onOk({
      mode: selectedMode,
      type: "image",
      uri: selectedImageUri,
      displayUrl: selectedImage?.displayUrl || null,
    });
    };

  const handlePick = (key, item) => {
    setSelectedImageKey(key);
    setSelectedImage(item);
    setSelectedImageUri(item?.rawUri || item?.raw?.imagePath || item?.raw?.gcs_uri || null);
  };

  const handleRevise = async (reviseText) => {
    setLoading(true);

    try {
      setLoadingDesc("Revising image...");

      const basePrompt =
        selectedImage?.raw?.prompt ||
        genConfig?.description ||
        "";

      const revisedPrompt = `${basePrompt}\n\nRevise: ${reviseText}`;

      const result = await generateSceneImage({
        promptText: revisedPrompt,
        numImages: 1,
      });

      const revisedOne = normalizeImages(result)[0];
      if (!revisedOne) return;

      setGeneratedImages((prev) => {
        const next = [...(prev ?? [])];
        const idx = next.findIndex((x, i) => {
          const key = x?.gcs_uri || x?.imagePath || x?.uri || String(i);
          return key === selectedImageKey;
        });

        if (idx >= 0) next[idx] = revisedOne;
        return next;
      });

      const nextKey =
        revisedOne?.gcs_uri ||
        revisedOne?.imagePath ||
        revisedOne?.uri ||
        null;

      setSelectedImageKey(nextKey);
      setSelectedImage(revisedOne);
      setSelectedImageUri(nextKey);
    } catch (e) {
      messageApi.error(e?.message ?? "Failed to revise image");
    } finally {
      setLoading(false);
      setLoadingDesc("");
    }
  };

  const handleImageUpload = async (list) => {
    if (!list.length) throw new Error("Please upload an image first.");

    setLoadingDesc("Uploading image..");
    const formData = new FormData();

    const file = list[list.length - 1];
    formData.append("files", file.originFileObj, file.name);

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/sessions/uploads/images`,
      {
        method: "POST",
        headers: { "X-Session-Token": sessionToken, "Authorization": `Bearer ${accessToken}` },
        body: formData,
      }
    );

    const data = await res.json();
    setLoadingDesc("");

    if (!res.ok) throw new Error(data?.message ?? "Upload failed");

    return (data?.data?.uploaded ?? []).map((f) => f.gcs_uri).filter(Boolean);
  };

  const handleUploadAndGoNext = async () => {
    const uris = await handleImageUpload(uploadFileList);

    const nextUploaded = uris.map((uri) => ({ gcs_uri: uri }));

    setUploadedImages(nextUploaded);

    setSelectedImageKey(null);
    setSelectedImage(null);
    setSelectedImageUri(null);

    setStep(1);
  };

  const onUploadChange = ({ fileList: next }) => {
    setUploadFileList((next ?? []).slice(-1));
  };

  const getLocalPreviewUrl = (file) => {
    const f = file?.originFileObj || file;
    if (!f) return "";
    return URL.createObjectURL(f);
  };

  const closeLocalPreview = () => {
    setLocalPreviewOpen(false);

    // cleanup blob url biar ga memory leak
    if (localPreviewSrc?.startsWith("blob:")) {
      URL.revokeObjectURL(localPreviewSrc);
    }
    setLocalPreviewSrc("");
  };

  const footer = (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <Button disabled={loading} onClick={onCancel}>Cancel</Button>

      {contextHolder}

      <div style={{ display: "flex", gap: 8 }}>
        <div>
          {step > 0 && (
            <Button disabled={loading} onClick={handleBack}>
              Back
            </Button>
          )}
        </div>

        {!isLast ? (
          <Button type="primary" onClick={handleNext} disabled={!canNext || loading}>
            Next
          </Button>
        ) : (
          <Button type="primary" onClick={handleUse} disabled={loading}>
            Use this
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={selectedMode ? footer : null}
      closable={false}
      width="60%"
      destroyOnHidden
    >
      <div className="modal-option-container">
        <div>Select option</div>
        <Select
          style={{ width: 240 }}
          placeholder="Choose options"
          value={selectedMode}
          onChange={(val) => setSelectedMode(val)}
          options={frameOptions}
        />
      </div>

      {loading ? (
        <div className='loading-container'>
          {loadingDesc}
          <Spin />
        </div>
      ) : (
        <>
          {selectedMode === "generate-image" && (
            <div>
              {step === 0 && (
                <Form
                  form={form}
                  initialValues={{
                    description: "",
                    num_images: 2,
                    aspect_ratio: "16:9",
                    apply_brand_guidelines: true,
                  }}
                  onValuesChange={() => {
                    setPrompt([]);
                    setSelectedPrompt([]);
                    setGeneratedImages([]);
                  }}
                >
                  <PromptGenerator
                    form={form}
                    fileList={refFileList}
                    setFileList={(next) => {
                      setRefFileList(next);
                      setPrompt([]);
                      setSelectedPrompt([]);
                      setGeneratedImages([]);
                    }}
                  />
                </Form>
              )}

              {step === 1 && (
                <PromptChoices
                  items={prompt}
                  revisingKey={revisingKey}
                  editingKey={editingKey}
                  onChangeSelected={(selectedTexts) => {
                    setSelectedPrompt(selectedTexts);
                    setGeneratedImages([]);
                  }}
                  onRevisePrompt={handleRevisePrompt}
                  onEditPrompt={handleEditPrompt}
                />
              )}

              {step === 2 && (
                <FrameImagePicker
                  images={generatedImages}
                  sessionId={sessionId}
                  title="Pick a frame"
                  value={selectedImageKey}
                  onChange={handlePick}
                  showRevise
                  onRevise={(text, raw) => handleRevise(text)}
                />
              )}
            </div>
          )}

          {selectedMode === "selected-image" && (
            <FrameImagePicker
              images={generatedImagesPrompt}
              sessionId={sessionId}
              title="Select from generated images"
              value={selectedImageKey}
              onChange={(key, item) => {
                setSelectedImageKey(key);
                setSelectedImage(item);
                const uri =
                  item?.raw?.imagePath ||
                  item?.raw?.gcs_uri ||
                  item?.raw?.uri ||
                  item?.displayUrl ||
                  null;
                setSelectedImageUri(uri);
              }}
              showRevise={false}
            />
          )}

          {selectedMode === "upload-image" && (
            <div>
              {step === 0 && (
                <div style={{ marginTop: 12 }}>
                  <Upload.Dragger
                    multiple={false}
                    maxCount={1}
                    accept="image/*"
                    fileList={uploadFileList}
                    beforeUpload={() => false}
                    onChange={onUploadChange}
                    listType="picture"
                    onPreview={(file) => {
                      const src = getLocalPreviewUrl(file);
                      setLocalPreviewSrc(src);
                      setLocalPreviewOpen(true);
                    }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag an image to upload</p>
                    <p className="ant-upload-hint">
                      You can preview before uploading
                    </p>
                  </Upload.Dragger>

                  <Modal
                    open={localPreviewOpen}
                    footer={null}
                    onCancel={closeLocalPreview}
                    centered
                    width="70vw"
                    styles={{ body: { padding: 0 } }}
                    destroyOnHidden
                  >
                    <img
                      src={localPreviewSrc}
                      alt="Local preview"
                      style={{ width: "100%", display: "block" }}
                    />
                  </Modal>
                </div>
              )}

              {step === 1 && (
                <FrameImagePicker
                  key={`upload-${uploadedImages.length}`}
                  images={uploadedImages}
                  sessionId={sessionId}
                  title="Preview uploaded image"
                  value={selectedImageKey}
                  onChange={(key, item) => {
                    setSelectedImageKey(key);
                    setSelectedImage(item);
                    setSelectedImageUri(
                      item?.rawUri ||
                      item?.raw?.gcs_uri ||
                      item?.raw?.imagePath ||
                      item?.raw?.uri ||
                      null
                    );
                  }}
                  showRevise={false}
                />
              )}
            </div>
          )}
        </>
      )
      }

    </Modal>
  );
}
